import type { FC } from "react"
import { useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Button, Stack, Typography, useMediaQuery, useTheme } from "@mui/material"
import { LocalizationProvider } from "@mui/x-date-pickers"
import type { DateTimeValidationError } from "@mui/x-date-pickers"
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon"
import { DateTime } from "luxon"

import type { EntityType } from "types/common.types"
import type { IDateRange } from "types/date.types"
import useDateConstraints, { usePickerLocale } from "../hooks"
import {
  formatForecastDuration,
  formatRangeDuration,
  getDateErrorMessage,
} from "../helpers"
import DateTimePanel from "../DateTimePanel"
import DateRangeField from "./DateRangeField"
import { RangeContext } from "./range"
import { PANEL_LINE } from "./constants"

type Field = "fromDate" | "toDate"

/** Two dated fields side by side do not fit a phone with the year in them. */
const COMPACT_FORMAT = "dd/MM HH:mm"

const FIELDS: { field: Field; labelKey: string }[] = [
  { field: "fromDate", labelKey: "generic.FROM" },
  { field: "toDate", labelKey: "generic.TO" },
]

interface DateRangeEditorProps {
  /** The range applied now, which the fields start from. */
  value: IDateRange
  /** Picking "Custom period" over a preset is a change even with the same dates. */
  isRangeChange: boolean
  entity: EntityType
  maxRangeMonths?: number
  onApply: (range: IDateRange) => void
  onCancel: () => void
}

const DateRangeEditor: FC<DateRangeEditorProps> = ({
  value,
  isRangeChange,
  entity,
  maxRangeMonths,
  onApply,
  onCancel,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMediumOrSmallDevice = useMediaQuery(theme.breakpoints.down("md"))
  const locale = usePickerLocale()
  const { maxDate, disableFuture, getTranslationParams } = useDateConstraints(entity)

  const [picked, setPicked] = useState<Record<Field, DateTime | null>>({
    fromDate: value.fromDate,
    toDate: value.toDate,
  })
  const [errors, setErrors] = useState<Record<Field, DateTimeValidationError>>({
    fromDate: null,
    toDate: null,
  })
  // The end the calendar and the clock edit; the editor opens on From.
  const [activeField, setActiveField] = useState<Field>("fromDate")

  const setField = (field: Field, date: DateTime | null) =>
    setPicked((current) => ({ ...current, [field]: date }))
  const setError = (field: Field, error: DateTimeValidationError) =>
    setErrors((current) => ({ ...current, [field]: error }))

  const { fromDate, toDate } = picked
  const rangeTooLong =
    !!maxRangeMonths &&
    !!fromDate &&
    !!toDate &&
    toDate > fromDate.plus({ months: maxRangeMonths })
  const isValid =
    !!fromDate && !!toDate && !errors.fromDate && !errors.toDate && !rangeTooLong
  const hasChanged =
    isRangeChange || !fromDate?.equals(value.fromDate) || !toDate?.equals(value.toDate)

  const duration = useMemo(() => {
    if (!fromDate?.isValid || !toDate?.isValid || toDate <= fromDate) return ""
    const total = formatRangeDuration(fromDate, toDate, locale)
    // The presets name their forecast with a chip; a custom range says it here.
    const now = DateTime.now()
    if (fromDate >= now) return `${total} ${t("dates.FORECAST_ONLY")}`
    const forecast = formatForecastDuration(toDate, locale, now)
    return forecast
      ? `${total} ${t("dates.FORECAST_DURATION", { duration: forecast })}`
      : total
  }, [fromDate, toDate, locale, t])

  const handleApply = useCallback(() => {
    if (isValid && hasChanged && fromDate && toDate) onApply({ fromDate, toDate })
  }, [isValid, hasChanged, fromDate, toDate, onApply])

  const range = useMemo(() => ({ fromDate, toDate }), [fromDate, toDate])
  const activeMinDate = activeField === "toDate" ? (fromDate ?? undefined) : undefined

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={locale}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={2}>
          {FIELDS.map(({ field, labelKey }) => (
            <DateRangeField
              key={field}
              label={t(labelKey)}
              value={picked[field]}
              onChange={(date) => setField(field, date)}
              onError={(error) => setError(field, error)}
              isActive={activeField === field}
              onActivate={() => setActiveField(field)}
              format={isMediumOrSmallDevice ? COMPACT_FORMAT : undefined}
              large={isMediumOrSmallDevice}
              disableFuture={disableFuture}
              maxDateTime={maxDate}
              minDateTime={field === "toDate" ? (fromDate ?? undefined) : undefined}
            />
          ))}
        </Stack>

        <RangeContext.Provider value={range}>
          <DateTimePanel
            value={picked[activeField]}
            onChange={(date) => setField(activeField, date)}
            disableFuture={disableFuture}
            minDate={activeMinDate}
            maxDate={maxDate}
          />
        </RangeContext.Provider>

        {/* Shown in every state: a snapshot can already break a limit. */}
        <Stack gap={1}>
          {FIELDS.map(
            ({ field, labelKey }) =>
              errors[field] && (
                <Typography key={field} color="error">
                  {t(labelKey)}:{" "}
                  {t(
                    getDateErrorMessage(errors[field]),
                    getTranslationParams(errors[field]),
                  )}
                </Typography>
              ),
          )}
          {rangeTooLong && (
            <Typography color="error">
              {t("csv_drawer.RANGE_TOO_LONG", { count: maxRangeMonths })}
            </Typography>
          )}
          {/* On a phone the buttons fill the bar, so the length goes above them. */}
          {isMediumOrSmallDevice && duration && (
            <Typography variant="body1" color="text.secondary">
              {duration}
            </Typography>
          )}
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          // On a phone the buttons stay pinned to the bottom of the dialog.
          sx={
            isMediumOrSmallDevice
              ? {
                  position: "sticky",
                  bottom: theme.spacing(-2),
                  // Out to the dialog's edges; the parent Stack's margin reset beats a plain mx.
                  "&&": { mx: -2 },
                  p: 2,
                  borderTop: 1,
                  borderColor: PANEL_LINE,
                  backgroundColor: "background.paper",
                  "& > .MuiButton-root": { flex: 1 },
                }
              : undefined
          }
        >
          {!isMediumOrSmallDevice && (
            <Typography variant="body1" color="text.secondary" sx={{ flex: 1 }}>
              {duration}
            </Typography>
          )}
          <Button
            color="inherit"
            variant={isMediumOrSmallDevice ? "outlined" : "text"}
            // Outlined on a phone, with the same line as the picker panels.
            sx={{ borderColor: PANEL_LINE }}
            onClick={onCancel}
          >
            {t("generic.CANCEL")}
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            disabled={!isValid || !hasChanged}
          >
            {t("generic.APPLY")}
          </Button>
        </Stack>
      </Stack>
    </LocalizationProvider>
  )
}

export default DateRangeEditor
